import { useState, useCallback } from 'react';
import { message as antdMessage } from 'antd';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { useErrorHandler } from './useErrorHandler';
import { validateFileAgainstPolicy, formatBytes } from '../Utils/fileUploadPolicy';
import {
    pickImageNative,
    shouldUseNativePicker,
    processUploadedFile,
    requestCameraPermissions
} from '../Utils/nativeFileUpload';

/**
 * Custom hook for handling file uploads with native mobile support
 * @param {Object} options - Hook options
 * @param {Function} options.uploadFunction - Function to upload file to server
 * @param {Object} options.policy - File upload policy
 * @param {boolean} options.imagesOnly - Only allow image files
 * @param {Function} options.onSuccess - Callback on successful upload
 * @param {Function} options.onError - Callback on error
 * @returns {Object} Upload handlers and state
 */
export const useNativeFileUpload = (options = {}) => {
    const {
        uploadFunction,
        policy,
        imagesOnly = false,
        onSuccess,
        onError,
    } = options;

    const { t } = useTranslation();
    const { handleError } = useErrorHandler();
    const [uploading, setUploading] = useState(false);
    const isNative = shouldUseNativePicker();

    /**
     * Validate file against policy
     * Shows user-friendly error messages using translation keys
     */
    const validateFile = useCallback((file) => {
        if (!file) return { valid: false, code: 'no_file' };

        try {
            const validation = validateFileAgainstPolicy(file, policy);
            if (!validation.valid) {
                // Show user-friendly error message
                if (validation.code === 'extension') {
                    const allowed = (validation.details?.allowed || []).join(', ');
                    antdMessage.error(t('file_type_not_allowed', { exts: allowed }));
                } else if (validation.code === 'size') {
                    const max = formatBytes(validation.details?.maxSize);
                    antdMessage.error(t('file_too_large_max', { max }));
                } else if (validation.code === 'name') {
                    const max = validation.details?.maxName;
                    antdMessage.error(t('filename_too_long_max', { max }));
                }
                return validation;
            }
            return validation;
        } catch (e) {
            console.error('File validation error:', e);
            return { valid: false, code: 'unknown' };
        }
    }, [policy, t]);

    /**
     * Handle file upload with native support
     * Uses handleError for API errors, custom onError callback if provided
     */
    const handleFileUpload = useCallback(async (file) => {
        if (!file) return null;

        // Validate against upload policy
        const validation = validateFile(file);
        if (!validation.valid) {
            return null;
        }

        setUploading(true);
        try {
            // Upload file to server
            const response = await uploadFunction(file);
            const fileData = response?.data || response;

            // Process the uploaded file for consistent structure (passes isNative for conditional thumbnail generation)
            const processedFile = await processUploadedFile(file, fileData, isNative);

            setUploading(false);

            // Call success callback if provided
            // Pass both the processed file and the full response (which may contain message_code)
            if (onSuccess) {
                onSuccess(processedFile, response);
            }

            return processedFile;
        } catch (error) {
            setUploading(false);

            // Call custom error callback if provided, otherwise use handleError
            if (onError) {
                onError(error);
            } else {
                // Use handleError for API errors with message_code
                handleError(error);
            }

            return null;
        }
    }, [uploadFunction, validateFile, onSuccess, onError, handleError]);

    /**
     * Handle native image picker
     * Shows custom translated messages ONLY for native permission/capability errors
     * Uses handleError for API errors
     */
    const handleNativeImagePick = useCallback(async (pickOptions = {}) => {
        if (!isNative || !imagesOnly) {
            return null; // Use standard HTML input
        }

        try {
            // Request permissions first
            const hasPermissions = await requestCameraPermissions();
            if (!hasPermissions) {
                // This is a native permission error, not an API error
                antdMessage.error(t('camera_permission_required'));
                return null;
            }

            // Pick image using native picker
            const file = await pickImageNative(pickOptions);

            if (!file) {
                return null; // User cancelled
            }

            // Upload the picked file (handleFileUpload will use handleError for API errors)
            return await handleFileUpload(file);
        } catch (error) {

            // Check if it's a native permission/capability error or an API error
            const isPermissionError = error.message && error.message.includes('permission');
            const isNativeError = !error.response; // API errors have error.response

            if (isNativeError) {
                // Native-specific errors: show custom translated message
                if (isPermissionError) {
                    antdMessage.error(t('camera_permission_denied'));
                } else {
                    antdMessage.error(t('failed_to_pick_image'));
                }
            } else {
                // API errors: use handleError for message_code support
                if (onError) {
                    onError(error);
                } else {
                    handleError(error);
                }
            }

            return null;
        }
    }, [isNative, imagesOnly, handleFileUpload, handleError, onError, t]);

    /**
     * Handler for Ant Design Upload component's beforeUpload
     * This intercepts the upload and handles it with our native-aware logic
     */
    const beforeUpload = useCallback(async (file) => {
        // For native platforms with images, we already handled this in handleNativeImagePick
        // For standard uploads, process the file
        await handleFileUpload(file);

        // Return false to prevent Ant Design from auto-uploading
        return false;
    }, [handleFileUpload]);

    /**
     * Custom upload handler that uses native picker on mobile
     */
    const customRequest = useCallback(async (pickOptions = {}) => {
        if (isNative && imagesOnly) {
            // Use native image picker
            return await handleNativeImagePick(pickOptions);
        } else {
            // Use standard HTML file input (handled by Ant Design Upload component)
            return null;
        }
    }, [isNative, imagesOnly, handleNativeImagePick]);

    return {
        uploading,
        isNative,
        handleFileUpload,
        handleNativeImagePick,
        beforeUpload,
        customRequest,
        validateFile,
    };
};
